import { Fragment } from 'react';
import { getMonthName, getReadableUnixTimeElements, isString } from '../GlobalFunctions';
import { values } from '../CheckIn/checkInValues';
import styles from './CheckIn.module.scss';

interface Props {
  isViewAssignmentSubmission?: boolean;
  teacherReview?: boolean;
  timeSubmitted: number;
  isViewDailyCheckIn?: boolean;
  studentWork?: string[];
  journalEntries?: string[];
  selectedReasons?: string[][];
  weekCheckInDays?: boolean[];
  handleResetSubmission?: () => void;
  handleGoHome?: () => void;
}

const DAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const ReviewCheckIn = ({
  isViewAssignmentSubmission,
  teacherReview,
  timeSubmitted,
  isViewDailyCheckIn,
  studentWork,
  journalEntries,
  selectedReasons,
  weekCheckInDays,
  handleResetSubmission,
  handleGoHome,
}: Props) => {
  const DARK_TEXT_COLORS = new Set(['#daeff1', '#fef8d8', '#f5c4b1']);

  const getContrastColor = (hexColor: string): string => {
    return DARK_TEXT_COLORS.has(hexColor.toLowerCase()) ? '#43484d' : '#fff';
  };

  const getTextColor = (feeling: string | undefined) => {
    if (feeling === undefined) return '#86939e';
    const color = values.find((value) => value.subEmotions.includes(feeling))?.subEmotionColor || '#e0e0e0';
    return getContrastColor(color);
  };

  const [year, , date, hour, min] = getReadableUnixTimeElements(timeSubmitted);
  const hourNumber = Number(hour);
  const minNumber = Number(min);
  const timeSubmittedString =
    timeSubmitted &&
    `${getMonthName(timeSubmitted)} ${date.toString().padStart(2, '0')}, ${year} @ ${hourNumber > 12 ? hourNumber - 12 : hour}:${
      minNumber < 10 ? `0${min}` : `${min}`
    } ${hourNumber > 11 ? 'pm' : 'am'}`;

  // journalEntries: all emotions submitted today (strings).
  // selectedReasons: parallel array of reason arrays, one per emotion submission.
  const todayEmotions: string[] =
    journalEntries && journalEntries.length > 0
      ? journalEntries
      : studentWork
        ? studentWork.filter((_, i) => i % 2 === 0)
        : [];

  // selectedReasons is the authoritative source — populated from the backend via retrieveJournalEntries
  // or from the current session. Fall back to parsing studentWork only for the legacy assignment flow.
  const todayReasonsByEmotion: string[][] =
    selectedReasons && selectedReasons.length > 0
      ? selectedReasons
      : studentWork
        ? todayEmotions.map((_, i) => {
            const r = studentWork[i * 2 + 1];
            return r
              ? Array.isArray(r)
                ? r
                : r
                    .split(',')
                    .map((s: string) => s.trim())
                    .filter(Boolean)
              : [];
          })
        : [];

  const today = new Date().getDay();

  /** Format ["a", "b"] → "a and b", ["a"] → "a" */
  const formatReasonList = (reasons: string[]): string => {
    if (reasons.length === 0) return '';
    if (reasons.length === 1) return reasons[0];
    return `${reasons.slice(0, -1).join(', ')} and ${reasons[reasons.length - 1]}`;
  };

  const weekCheckins = weekCheckInDays ?? [false, false, false, false, false, false, false];

  /* ---- Shared "You did it!" card used for both daily and assignment views ---- */
  const renderYouDidItCard = () => (
    <div className={styles.reviewContainer}>
      <h2 className={styles.reviewHeading}>You did it!</h2>

      <div className={styles.reviewCard}>
        <p className={styles.weekStripTitle}>Your Check-Ins This Week</p>
        {/* Weekly strip */}
        <div className={styles.weekStripSection}>
          <div className={styles.weekStrip}>
            {DAY_LABELS.map((label, i) => (
              <div key={i} className={styles.dayColumn}>
                <span className={`${styles.dayLabel} ${i === today ? styles.dayLabelToday : ''}`}>{label}</span>
                <div className={`${styles.dayDot} ${weekCheckins[i] ? styles.dayDotChecked : ''}`}>
                  {weekCheckins[i] && (
                    <svg viewBox='0 0 24 24' fill='none' className={styles.checkIcon}>
                      <path
                        d='M5 13l4 4L19 7'
                        stroke='#fff'
                        strokeWidth='2.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Emotion summary section */}
        {todayEmotions.length > 0 && (
          <div className={styles.emotionSummarySection}>
            <div className={styles.emotionBadgeRow}>
              {todayEmotions.map((emotion, i) => {
                const bg = values.find((v) => v.subEmotions.includes(emotion))?.subEmotionColor || '#fef8d8';
                return (
                  <div key={`${emotion}${i}`} className={styles.emotionSummaryBadge} style={{ backgroundColor: bg }}>
                    <span style={{ color: getTextColor(emotion) }}>{emotion.replace('/', ' / ')}</span>
                  </div>
                );
              })}
            </div>
            <div className={styles.emotionSummaryLines}>
              {todayEmotions.map((emotion, i) => {
                const reasons = todayReasonsByEmotion[i] ?? [];
                const reasonText = formatReasonList(reasons);
                return (
                  <p key={`${emotion}${i}`} className={styles.emotionSummaryText}>
                    {'You were feeling '}
                    <strong>{emotion.replace('/', ' / ')}</strong>
                    {reasonText ? (
                      <>
                        {' because of '}
                        <strong>{reasonText}</strong>
                      </>
                    ) : null}
                  </p>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Action buttons */}
      {!teacherReview && (
        <div className={styles.reviewButtonStack}>
          <button className={styles.reviewPrimaryButton} onClick={handleGoHome}>
            View To Dos <span className={styles.arrowIcon}>→</span>
          </button>
          <button className={styles.reviewSecondaryButton} onClick={handleResetSubmission}>
            Submit Another Emotion <span className={styles.arrowIcon}>→</span>
          </button>
        </div>
      )}
      {teacherReview && timeSubmitted && (
        <p className={styles.submittedAt}>Student completed this on {timeSubmittedString}.</p>
      )}
    </div>
  );

  return (
    <>
      {isViewAssignmentSubmission &&
        (teacherReview ? (
          <div className={styles.assignmentSubmissionContainer}>
            <h3 className={styles.teacherReviewHeading}>Student Check In Response</h3>
            <div className={styles.responseRow}>
              {studentWork &&
                studentWork.map((item, index) => {
                  const isEmotion = index === 0;
                  const emotionColor = isEmotion
                    ? values.find((value) => value.subEmotions.includes(item))?.segmentColor || '#bbc2ca'
                    : '#e8edf2';
                  return (
                    isString(item) && (
                      <div
                        key={`${item}${index}`}
                        className={styles.emotionContainer}
                        style={{ backgroundColor: emotionColor }}
                      >
                        <h3
                          className={styles.emotionText}
                          style={{ color: isEmotion ? getTextColor(item) : '#43484d' }}
                        >
                          {item.replace('/', ' / ') || '-'}
                        </h3>
                      </div>
                    )
                  );
                })}
            </div>
            <p>Student completed this assignment on {timeSubmittedString}.</p>
          </div>
        ) : (
          renderYouDidItCard()
        ))}
      {isViewDailyCheckIn && journalEntries && <Fragment key={'text response'}>{renderYouDidItCard()}</Fragment>}
    </>
  );
};

export default ReviewCheckIn;
